import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { database } from '../firebase';
import { FaBell } from 'react-icons/fa';
import WhatsAppReminderTemplate from '../components/WhatsAppReminderTemplate';
import { getDocumentList } from '../forms/loanTypes';

const Reminders = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    urgentReminders: 0,
    totalPendingDocs: 0,
    birthdaysToday: 0,
    birthdaysThisWeek: 0,
    birthdaysThisMonth: 0,
    insuranceExpiringSoon: 0
  });
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const navigate = useNavigate();

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const customersRef = ref(database, 'customers');
      const remindersRef = ref(database, 'reminders');
      const birthdatesRef = ref(database, 'birthdates');
      const insurencesRef = ref(database, 'insurences');

      const [customersSnapshot, remindersSnapshot, birthdatesSnapshot, insurencesSnapshot] = await Promise.all([
        get(customersRef),
        get(remindersRef),
        get(birthdatesRef),
        get(insurencesRef)
      ]);

      // Process Customers
      const customersData = customersSnapshot.exists() ? customersSnapshot.val() : {};
      const customerList = [];
      let urgentCount = 0;
      let totalPendingDocs = 0;
      Object.entries(customersData).forEach(([id, data]) => {
        // Calculate pending docs dynamically to ensure consistency with Customer Details
        const loanType = data.loanType ||
          data.loan_application?.loan_details?.loan_type ||
          data.loan_type || '';

        const occupation = data.customer_details?.occupation_info || {};
        const basic = data.customer_details?.basic_info || {};
        const employmentType = occupation.type || occupation.occupation_type || basic.occupation_type || '';

        const requiredDocs = getDocumentList(loanType, employmentType) || [];
        const collectedDocsMap = data.loan_application?.documents?.collected_documents || {};

        // 1. Calculate pending required documents (using sanitized keys)
        const pendingRequired = requiredDocs.filter(docName => {
          const key = docName.replace(/[^a-zA-Z0-9]/g, '');
          const isCollected = collectedDocsMap[key] === true || collectedDocsMap[key] === 'true';
          return !isCollected;
        });

        // 2. Calculate pending extra documents
        const extraDocs = data.loan_application?.documents?.extra_documents || [];
        const pendingExtra = extraDocs.filter(doc => !doc.collected).map(doc => doc.name);

        // Combine and distinct
        const allPendingDocs = [...new Set([...pendingRequired, ...pendingExtra])];

        if (allPendingDocs.length > 0) {
          customerList.push({
            id,
            name: data.customer_details?.basic_info?.full_name || data.fullName || 'Unknown',
            mobile: data.customer_details?.basic_info?.mobile || data.mobile1 || '',
            pendingDocs: allPendingDocs.length,
            documents: allPendingDocs
          });
          totalPendingDocs += allPendingDocs.length;
          if (allPendingDocs.length >= 3) urgentCount++;
        }
      });

      // Process Reminders
      let pendingRemindersCount = 0;
      const remindersData = remindersSnapshot.exists() ? remindersSnapshot.val() : {};

      Object.values(remindersData).forEach(dateGroups => {
        Object.values(dateGroups).forEach(items => {
          Object.values(items).forEach(item => {
            if (item.status !== 'completed') {
              pendingRemindersCount++;
            }
          });
        });
      });

      // Process Birthdays
      let birthdaysToday = 0;
      let birthdaysThisWeek = 0;
      let birthdaysThisMonth = 0;

      if (birthdatesSnapshot.exists()) {
        const birthdatesData = birthdatesSnapshot.val();
        const today = new Date();

        Object.values(birthdatesData).forEach(data => {
          if (data.birthdate) {
            const bday = new Date(data.birthdate);

            // Check if birthday is today
            if (today.getMonth() === bday.getMonth() && today.getDate() === bday.getDate()) {
              birthdaysToday++;
            }

            // Check if birthday is this month
            if (today.getMonth() === bday.getMonth()) {
              birthdaysThisMonth++;

              // Check if birthday is this week
              const dayOfYear = (date) => Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
              const diff = dayOfYear(new Date(today.getFullYear(), bday.getMonth(), bday.getDate())) - dayOfYear(today);
              if (diff >= 0 && diff <= 7) {
                birthdaysThisWeek++;
              }
            }
          }
        });
      }

      // Process Insurance
      let insuranceExpiringSoon = 0;

      if (insurencesSnapshot.exists()) {
        const insurencesData = insurencesSnapshot.val();
        const today = new Date();

        Object.values(insurencesData).forEach(data => {
          if (data.endDate) {
            const expiryDate = new Date(data.endDate);
            const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

            // Count policies expiring within 30 days
            if (daysLeft >= 0 && daysLeft <= 30) {
              insuranceExpiringSoon++;
            }
          }
        });
      }

      setCustomers(customerList);
      setStats({
        totalCustomers: customerList.length,
        urgentReminders: urgentCount,
        totalPendingDocs,
        pendingReminders: pendingRemindersCount,
        birthdaysToday,
        birthdaysThisWeek,
        birthdaysThisMonth,
        insuranceExpiringSoon
      });
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const getPendingDocumentsDetails = () => {
    // Scroll to customer list
    document.getElementById('customer-list')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 px-4 sm:px-6 lg:px-8 py-8">
      <div className="w-full space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
          <div className="space-y-2 w-full md:w-auto">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg transform hover:rotate-12 transition-transform duration-300">
                <FaBell className="text-white text-lg sm:text-xl" />
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent tracking-tight">
                Reminders & Notifications
              </h1>
            </div>
            <p className="text-gray-600 ml-13 sm:ml-15 flex items-center gap-2 text-sm sm:text-base">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Stay updated with pending tasks, document collection, and deadlines
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            onClick={() => navigate('/follow-up-reminders')}>
            <div className="text-4xl font-bold mb-2">{stats.pendingReminders || 0}</div>
            <div className="text-orange-100 font-medium">Customers Reminders</div>
            <div className="text-orange-200 text-sm mt-1">Pending Follow-ups</div>
          </div>

          <div
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            onClick={getPendingDocumentsDetails}
          >
            <div className="text-4xl font-bold mb-2">{stats.totalPendingDocs}</div>
            <div className="text-blue-100 font-medium">Total Pending Documents</div>
            <div className="text-blue-200 text-sm mt-2 flex items-center gap-1">
              <span>👆</span> Click to view details
            </div>
          </div>
          <div
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            onClick={() => navigate('/birthday-reminders')}
          >
            <div className="text-4xl font-bold mb-2">{stats.birthdaysThisMonth || 0}</div>
            <div className="text-purple-100 font-medium">Birthday Reminders</div>
            <div className="text-purple-200 text-sm mt-1">
              {stats.birthdaysToday > 0 && <div>🎉 {stats.birthdaysToday} today</div>}
              {stats.birthdaysThisWeek > 0 && <div>� {stats.birthdaysThisWeek} this week</div>}
              {stats.birthdaysThisMonth === 0 && <div>This Month</div>}
            </div>
          </div>
          <div
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            onClick={() => navigate('/insurance-reminders')}
          >
            <div className="text-4xl font-bold mb-2">{stats.insuranceExpiringSoon || 0}</div>
            <div className="text-green-100 font-medium">Insurance Reminders</div>
            <div className="text-green-200 text-sm mt-1">Expiring Soon (30 days)</div>
          </div>
        </div>

        {/* Customer List */}
        <div id="customer-list" className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <h3 className="text-xl font-bold text-gray-800">Pending Document List</h3>
          </div>

          {customers.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <span className="text-4xl block mb-4">✅</span>
              <p className="text-lg">No pending documents! All customers are up to date.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {customers.map((customer) => (
                <div key={customer.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-bold text-gray-800">{customer.name}</h4>
                        <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full">
                          {customer.pendingDocs} pending
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <span>📱</span> {customer.mobile}
                        </span>
                        <span className="flex items-center gap-1">
                          <span>📄</span> Documents: {customer.documents.join(', ')}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setShowWhatsAppModal(true);
                      }}
                      className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 font-medium transition-all flex items-center gap-2"
                    >
                      <span>📱</span> WhatsApp
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* WhatsApp Modal */}
        {showWhatsAppModal && selectedCustomer && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">{selectedCustomer.name}</h3>
                    <p className="text-blue-100 mt-1">Pending Documents Reminder</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowWhatsAppModal(false);
                      setSelectedCustomer(null);
                    }}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <span className="text-2xl">✕</span>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Customer Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-bold text-gray-800 mb-3">Customer Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-600">📱 Mobile:</span>
                      <span className="text-gray-800">{selectedCustomer.mobile}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-600">📄 Pending Documents:</span>
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full font-bold">
                        {selectedCustomer.pendingDocs}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Pending Documents List */}
                <div className="bg-orange-50 rounded-xl p-4">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <span>📋</span> Documents Needed
                  </h4>
                  <div className="space-y-2">
                    {selectedCustomer.documents.map((doc, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm bg-white p-2 rounded-lg">
                        <span className="text-orange-600">•</span>
                        <span className="text-gray-800">{doc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* WhatsApp Message Preview */}
                <div className="bg-green-50 rounded-xl p-4">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <span>💬</span> Message Preview
                  </h4>
                  <div className="bg-white p-4 rounded-lg text-sm text-gray-700 whitespace-pre-line border border-green-200">
                    {`Dear ${selectedCustomer.name},

We hope this message finds you well.

We would like to remind you that the following documents are still pending for your loan application:

${selectedCustomer.documents.map((doc, i) => `${i + 1}. ${doc}`).join('\n')}

Please submit these documents at your earliest convenience to proceed with your application.

For any queries, feel free to contact us.

Thank you,
Vardhman Financial Services
📱 9270 355 171`}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      const message = `Dear ${selectedCustomer.name},\n\nWe hope this message finds you well.\n\nWe would like to remind you that the following documents are still pending for your loan application:\n\n${selectedCustomer.documents.map((doc, i) => `${i + 1}. ${doc}`).join('\n')}\n\nPlease submit these documents at your earliest convenience to proceed with your application.\n\nFor any queries, feel free to contact us.\n\nThank you,\nVardhman Financial Services\n📱 9270 355 171`;
                      const whatsappUrl = `https://wa.me/91${selectedCustomer.mobile}?text=${encodeURIComponent(message)}`;
                      window.open(whatsappUrl, '_blank');
                    }}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 font-bold shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <span>📱</span> Send via WhatsApp
                  </button>
                  <button
                    onClick={() => navigate(`/customer/${selectedCustomer.id}`)}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                  >
                    View Full Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reminders;
